/**
 * Kanban board for tickets.
 * Groups tickets by status / priority / assignee and renders compact cards.
 */
import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { MapPin, MessageSquare, Paperclip, Clock } from 'lucide-react';
import { StatusBadge, PriorityBadge, SlaBadge, stripHtml, formatDateCompact } from './TicketComponents';
import { internalStatusBadgeClasses, formatDueDate, dueDateRelative } from '../../lib/ticketFields';

const STATUS_COLUMNS = [
  { key: 'open', label: 'Open', color: 'bg-amber-50 text-amber-800' },
  { key: 'in_progress', label: 'In Progress', color: 'bg-blue-50 text-blue-800' },
  { key: 'waiting_on_customer', label: 'Waiting', color: 'bg-purple-50 text-purple-800' },
  { key: 'under_investigation', label: 'Investigating', color: 'bg-cyan-50 text-cyan-800' },
  { key: 'resolved', label: 'Resolved', color: 'bg-emerald-50 text-emerald-800' },
];

const PRIORITY_COLUMNS = [
  { key: 'critical', label: 'Critical', color: 'bg-rose-50 text-rose-800' },
  { key: 'high', label: 'High', color: 'bg-orange-50 text-orange-800' },
  { key: 'medium', label: 'Medium', color: 'bg-blue-50 text-blue-800' },
  { key: 'low', label: 'Low', color: 'bg-slate-100 text-slate-800' },
];

function TicketCard({ ticket, onSelect, internalStatuses = [] }) {
  const subject =
    (ticket.subject || '').trim() ||
    stripHtml(ticket.text).split('\n').filter(Boolean)[0] ||
    stripHtml(ticket.text);
  const currentInternal = internalStatuses.find((i) => i.id === ticket.internal_status);
  const dueOverdue = ticket.due_date && new Date(ticket.due_date).getTime() < Date.now() && ticket.status !== 'resolved';

  return (
    <button
      type="button"
      onClick={() => onSelect(ticket.id)}
      className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-indigo-300 hover:shadow transition-all space-y-2"
      data-testid={`kanban-card-${ticket.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Badge
          variant="outline"
          className="bg-slate-900 text-white border-slate-900 font-mono text-[10px]"
        >
          #{ticket.ticket_number}
        </Badge>
        <PriorityBadge priority={ticket.priority} />
      </div>

      {(ticket.internal_only || ticket.on_behalf_of_actor_name) && (
        <div className="flex flex-wrap gap-1">
          {ticket.internal_only && (
            <Badge
              variant="outline"
              className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] font-semibold gap-1"
              title="Agency-only ticket"
            >
              <span className="w-1 h-1 rounded-full bg-amber-500" />
              INTERNAL
            </Badge>
          )}
          {ticket.on_behalf_of_actor_name && (
            <Badge
              variant="outline"
              className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]"
              title={`On behalf of ${ticket.created_by_name}`}
            >
              on behalf of {ticket.created_by_name}
            </Badge>
          )}
        </div>
      )}

      <div className="text-sm font-medium text-slate-800 line-clamp-2">
        {subject || '(no subject)'}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {ticket.location_name}
        </span>
        <span>{formatDateCompact(ticket.created_at)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <StatusBadge status={ticket.status} />
        <SlaBadge
          breached={ticket.sla_breached}
          paused={ticket.sla_paused}
          dueAt={ticket.sla_due_at}
          hours={ticket.sla_hours}
        />
        {currentInternal && (
          <Badge
            variant="outline"
            className={`${internalStatusBadgeClasses(currentInternal.color)} text-[10px] gap-1`}
            title={`Internal status: ${currentInternal.label}`}
            data-testid="kanban-internal-status-badge"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            {currentInternal.label}
          </Badge>
        )}
        {ticket.due_date && (
          <Badge
            variant="outline"
            className={`${dueOverdue
              ? 'bg-rose-50 text-rose-700 border-rose-200'
              : 'bg-sky-50 text-sky-700 border-sky-200'} text-[10px] gap-1`}
            title={`Estimated delivery: ${formatDueDate(ticket.due_date)} (${dueDateRelative(ticket.due_date)})`}
            data-testid="kanban-due-date-badge"
          >
            <Clock className="w-3 h-3" />
            {formatDueDate(ticket.due_date)}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        {ticket.assigned_to_name || ticket.assigned_to_email ? (
          <span className="truncate">
            {ticket.assigned_to_name || ticket.assigned_to_email}
          </span>
        ) : (
          <span className="italic">Unassigned</span>
        )}
        {ticket.replies?.length > 0 && (
          <span className="inline-flex items-center gap-1 ml-auto">
            <MessageSquare className="w-3 h-3" />
            {ticket.replies.length}
          </span>
        )}
        {ticket.attachments?.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <Paperclip className="w-3 h-3" />
            {ticket.attachments.length}
          </span>
        )}
      </div>
    </button>
  );
}

export function TicketKanban({ tickets, groupBy = 'status', assignees = [], onSelectTicket, internalStatuses = [] }) {
  const columns = useMemo(() => {
    if (groupBy === 'priority') return PRIORITY_COLUMNS;
    if (groupBy === 'assignee') {
      const byId = new Map();
      byId.set('unassigned', { key: 'unassigned', label: 'Unassigned', color: 'bg-slate-100 text-slate-800' });
      assignees.forEach((a) => {
        byId.set(a.id, {
          key: a.id,
          label: a.name || a.email,
          color: 'bg-indigo-50 text-indigo-800',
        });
      });
      // also include any assignees that appear on tickets but not in list
      tickets.forEach((t) => {
        const id = t.assigned_to_id || '';
        if (id && !byId.has(id)) {
          byId.set(id, {
            key: id,
            label: t.assigned_to_name || t.assigned_to_email || 'Unknown',
            color: 'bg-indigo-50 text-indigo-800',
          });
        }
      });
      return Array.from(byId.values());
    }
    return STATUS_COLUMNS;
  }, [groupBy, assignees, tickets]);

  const ticketsByColumn = useMemo(() => {
    const map = {};
    columns.forEach((c) => {
      map[c.key] = [];
    });
    tickets.forEach((t) => {
      let key;
      if (groupBy === 'status') key = t.status;
      else if (groupBy === 'priority') key = t.priority;
      else key = t.assigned_to_id || 'unassigned';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [tickets, columns, groupBy]);

  return (
    <div className="h-full overflow-x-auto snap-x snap-mandatory p-2 sm:p-4" data-testid="ticket-kanban">
      <div className="flex gap-3 sm:gap-4 min-w-max h-full">
        {columns.map((col) => {
          const colTickets = ticketsByColumn[col.key] || [];
          return (
            <div
              key={col.key}
              className="w-[85vw] max-w-[320px] sm:w-80 flex-shrink-0 flex flex-col bg-slate-50 rounded-lg border border-slate-200 snap-start"
              data-testid={`kanban-column-${col.key}`}
            >
              <div className={`px-3 py-2 rounded-t-lg flex items-center justify-between sticky top-0 z-10 ${col.color}`}>
                <span className="text-sm font-semibold">{col.label}</span>
                <Badge variant="outline" className="bg-white/70 text-slate-700">
                  {colTickets.length}
                </Badge>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {colTickets.length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-6">No tickets</div>
                ) : (
                  colTickets.map((t) => (
                    <TicketCard key={t.id} ticket={t} onSelect={onSelectTicket} internalStatuses={internalStatuses} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
